import { useEffect, useMemo, useState } from "react";
import { Download, Loader2, Monitor, Apple } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const isMac = (os) => /mac|darwin/i.test(os || "");

const AppTile = ({ a, on, toggle }) => (
  <label className={`flex items-center gap-2.5 rounded-md border px-3 py-2 cursor-pointer transition-colors ${on ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`sw-${a.key}`}>
    <Checkbox checked={on} onCheckedChange={() => toggle(a.key)} />
    <img src={a.icon} alt="" className="h-5 w-5 rounded-sm" loading="lazy" />
    <span className="text-sm truncate mr-auto">{a.name}</span>
    {!a.mac && <span className="text-[11px] font-medium text-slate-600 bg-slate-100 rounded px-1.5" title="Windows uniquement">Win</span>}
    {!a.windows && <span className="text-[11px] font-medium text-slate-600 bg-slate-100 rounded px-1.5" title="Mac uniquement">Mac</span>}
  </label>
);

const Targets = ({ rows, sel, setSel }) => {
  const groups = useMemo(() => [...new Set(rows.map((r) => r.group).filter(Boolean))].sort(), [rows]);
  const pickGroup = (g) => setSel(rows.filter((r) => r.online && (g === "*" || r.group === g)).map((r) => r.machine_id));
  const toggle = (id) => setSel((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="sw-targets">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider text-slate-500">Postes</p>
        {sel.length > 0 && <button type="button" className="text-xs text-slate-500 hover:text-slate-800 hover:underline" onClick={() => setSel([])} data-testid="sw-targets-clear">Tout décocher ({sel.length})</button>}
      </div>
      <Select value="" onValueChange={pickGroup}>
        <SelectTrigger className="h-9" data-testid="sw-client-select"><SelectValue placeholder="Tout un client…" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="*">Tous les postes en ligne</SelectItem>
          {groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="max-h-80 overflow-auto divide-y divide-slate-100">
        {rows.map((r) => {
          const on = sel.includes(r.machine_id);
          const usable = r.online || on;
          return (
          <label key={r.machine_id} className={`flex items-center gap-2 py-1.5 text-sm ${usable ? "cursor-pointer" : "opacity-50"}`} data-testid={`sw-target-${r.machine_id}`}>
            <Checkbox checked={on} disabled={!usable} onCheckedChange={() => toggle(r.machine_id)} data-testid={`sw-target-check-${r.machine_id}`} />
            {isMac(r.os) ? <Apple className="h-3.5 w-3.5 text-slate-400" /> : <Monitor className="h-3.5 w-3.5 text-slate-400" />}
            <span className="truncate mr-auto">{r.name}</span>
            <span className="text-xs text-slate-400 truncate max-w-[40%]">{r.online ? r.group : "hors ligne"}</span>
          </label>
          );
        })}
      </div>
    </div>
  );
};

export const SoftwareCatalog = ({ rows }) => {
  const [apps, setApps] = useState([]);
  const [keys, setKeys] = useState([]);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/software/catalog").then(({ data }) => setApps(data)).catch((e) => toast.error(errMsg(e))); }, []);
  const cats = useMemo(() => apps.reduce((acc, a) => ({ ...acc, [a.category]: [...(acc[a.category] || []), a] }), {}), [apps]);
  const sorted = useMemo(() => [...rows].sort((a, b) => (b.online - a.online) || a.name.localeCompare(b.name)), [rows]);
  const toggle = (k) => setKeys((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));
  const install = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/software/install", { machine_ids: sel, keys });
      if (data.started.length) toast.success(`Installation lancée sur ${data.started.length} poste(s). Suivez le résultat dans l'historique de chaque poste.`);
      if (data.skipped.length) {
        const by = data.skipped.reduce((acc, s) => ({ ...acc, [s.reason]: [...(acc[s.reason] || []), s.name] }), {});
        toast.warning(`${data.skipped.length} poste(s) ignoré(s)`, {
          description: Object.entries(by).map(([r, n]) => `${r} : ${n.slice(0, 5).join(", ")}${n.length > 5 ? ` +${n.length - 5}` : ""}`).join(" · "), duration: 10000 });
      }
      if (data.started.length) setKeys([]);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-5" data-testid="software-catalog">
      <div className="space-y-5">
        <p className="text-sm text-slate-600">Cochez les logiciels puis les postes : ils s'installent en silence, dans leur dernière version, sans aucune fenêtre pour l'utilisateur.</p>
        {Object.entries(cats).map(([cat, list]) => (
          <section key={cat}>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{cat}</p>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2">{list.map((a) => <AppTile key={a.key} a={a} on={keys.includes(a.key)} toggle={toggle} />)}</div>
          </section>
        ))}
      </div>
      <div className="space-y-3 lg:sticky lg:top-4 self-start">
        <Targets rows={sorted} sel={sel} setSel={setSel} />
        <Button className="w-full h-11" disabled={busy || !keys.length || !sel.length} onClick={install} data-testid="sw-install-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Installer {keys.length} logiciel(s) sur {sel.length} poste(s)
        </Button>
        <p className="text-xs text-slate-500">Mac : nécessite Homebrew sur le poste. Les logiciels marqués « Win » ou « Mac » existent seulement pour ce système.</p>
      </div>
    </div>
  );
};
